import React, { useState, useEffect, useRef, useMemo } from 'react';
import { sanitizeSvg } from '../lib/sanitizeSvg';
import axios from 'axios';
import { Button } from './ui/button';
import { Upload, RotateCcw, X, Move } from 'lucide-react';
import { toast } from 'sonner';

/**
 * VectorDesignEditor
 * ------------------
 * Self-contained SVG color editor + raster preview fallback.
 *
 * Props:
 *  - vectorDesign:     existing { format, svg_content, original_svg, colors, placements, ... } | null
 *  - proposalId:       proposal UUID
 *  - itemIndex:        index into proposal.items
 *  - onUpdate(vd):     parent state sync callback
 *  - mode:             'admin' (upload + recolor + placements) | 'public' (recolor only)
 *  - locations:        array of strings ['front','back','left_sleeve',...] for placement tabs
 *  - inlineApi:        OPTIONAL — `{ patch: (body) => Promise<vd> }` to override the
 *                      default REST call (used by Public page that posts unauthenticated).
 *
 * Renders nothing fancy: a drop zone, the SVG, a swatch palette under it,
 * a color picker that fires inline. Click any path in the SVG to recolor
 * just that element. Click a palette swatch to recolor every element that
 * shares that fill/stroke.
 */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function normalizeHex(v) {
  if (!v || typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (s.startsWith('#')) {
    if (s.length === 7) return s;
    if (s.length === 4) return '#' + s.slice(1).split('').map((c) => c + c).join('');
  }
  if (s.startsWith('rgb')) {
    const nums = s.match(/\d+(?:\.\d+)?/g);
    if (nums && nums.length >= 3) {
      const [r, g, b] = nums.slice(0, 3).map((n) => Math.max(0, Math.min(255, Math.round(parseFloat(n)))));
      return '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('');
    }
  }
  return null;
}

const VectorDesignEditor = ({
  vectorDesign,
  proposalId,
  itemIndex,
  onUpdate,
  mode = 'admin',
  locations = ['front', 'back'],
  inlineApi = null,
  locked = false,
}) => {
  const vd = vectorDesign || null;
  const [activeLoc, setActiveLoc] = useState((locations && locations[0]) || 'front');
  const [picker, setPicker] = useState(null); // { oldColor, newColor }
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Local override for placement inputs while the user is typing — we
  // commit to the server (and thus to props) onBlur. This avoids a remote
  // round-trip per keystroke without re-introducing the prop-mirror anti-
  // pattern (we only override the single field being edited).
  const [pendingPlacement, setPendingPlacement] = useState(null);
  const svgWrapRef = useRef(null);

  const isAdmin = mode === 'admin';
  const isSvg = vd && vd.format === 'svg';
  const colors = (vd && vd.colors) || [];

  // Render SVG inline via dangerouslySetInnerHTML so click handlers can be
  // attached after mount. Cap raw SVG size so a maliciously large file can't
  // freeze the editor.
  const safeSvg = useMemo(() => {
    if (!isSvg || !vd.svg_content) return '';
    const raw = vd.svg_content;
    if (raw.length > 1024 * 1024) return '<!-- SVG too large to render inline -->';
    return sanitizeSvg(raw);
  }, [vd, isSvg]);

  // ===== Upload handler ===========================================
  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large (max 8 MB)');
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(',')[1];
      const res = await axios.post(
        `/proposals/${proposalId}/items/${itemIndex}/vector-design`,
        { filename: file.name, mime_type: file.type || '', content_base64: base64 }
      );
      onUpdate && onUpdate(res.data.vector_design);
      toast.success(
        res.data.vector_design.format === 'svg'
          ? 'SVG uploaded — palette ready'
          : 'File uploaded (vector preview — not color-editable)'
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ===== Apply color change ========================================
  const applyColorChange = async (oldHex, newHex) => {
    const nOld = normalizeHex(oldHex);
    const nNew = normalizeHex(newHex);
    if (!nOld || !nNew || nOld === nNew) return;
    setSaving(true);
    try {
      const body = { color_map: { [nOld]: nNew } };
      const data = inlineApi
        ? await inlineApi.patch(body)
        : (await axios.patch(`/proposals/${proposalId}/items/${itemIndex}/vector-design`, body)).data.vector_design;
      onUpdate && onUpdate(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recolor failed');
    } finally {
      setSaving(false);
      setPicker(null);
    }
  };

  // ===== Reset to original =========================================
  const handleReset = async () => {
    setSaving(true);
    try {
      const data = inlineApi
        ? await inlineApi.patch({ reset: true })
        : (await axios.patch(`/proposals/${proposalId}/items/${itemIndex}/vector-design`, { reset: true })).data.vector_design;
      onUpdate && onUpdate(data);
      toast.success('Colors reset to original');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  // ===== Click-to-pick on a specific element =======================
  // We attach a one-shot listener whenever the SVG node renders. Clicking
  // a child with a fill/stroke opens the color picker scoped to that
  // element's color — recoloring will swap every same-colored element
  // (matches the palette behavior). One-element-only recoloring would need
  // server-side element ids; deferred to v2.
  useEffect(() => {
    if (!svgWrapRef.current || !isSvg) return;
    const root = svgWrapRef.current.querySelector('svg');
    if (!root) return;
    const onClick = (e) => {
      const el = e.target;
      if (!el || el === root) return;
      const raw =
        el.getAttribute('fill') ||
        (el.getAttribute('style') || '').match(/fill:\s*([^;]+)/i)?.[1] ||
        el.getAttribute('stroke') ||
        '';
      const norm = normalizeHex(raw);
      if (!norm) return;
      setPicker({ oldColor: norm, newColor: norm });
    };
    root.style.cursor = 'pointer';
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [safeSvg, isSvg]);

  // ===== Placements (admin-only, simple inputs) ====================
  const placements = (vd && vd.placements) || {};
  const savedPlacement = placements[activeLoc] || { x: 50, y: 35, scale: 30, rotation: 0 };
  // Use pending overrides while the user is typing so the input is
  // responsive; reset on activeLoc change so a tab switch shows the saved
  // values for the new location.
  const placement = pendingPlacement && pendingPlacement.loc === activeLoc
    ? { ...savedPlacement, ...pendingPlacement.values }
    : savedPlacement;

  const savePlacement = async (next) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const body = { placements: { [activeLoc]: next } };
      const res = await axios.patch(`/proposals/${proposalId}/items/${itemIndex}/vector-design`, body);
      setPendingPlacement(null);
      onUpdate && onUpdate(res.data.vector_design);
    } catch (e) {
      toast.error('Failed to save placement');
    } finally {
      setSaving(false);
    }
  };

  // ===== Render ====================================================
  if (!vd) {
    if (!isAdmin) {
      return null; // Public view: nothing to edit if there's no SVG yet
    }
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
        <input
          type="file"
          accept=".svg,image/svg+xml,.ai,.eps,application/postscript,application/illustrator,application/pdf"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          style={{ display: 'none' }}
          id={`vd-upload-${itemIndex}`}
          data-testid={`vector-design-upload-input-${itemIndex}`}
        />
        <label
          htmlFor={`vd-upload-${itemIndex}`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md cursor-pointer text-sm"
          data-testid={`vector-design-upload-btn-${itemIndex}`}
        >
          <Upload size={16} />
          {uploading ? 'Uploading…' : 'Upload Vector Design (.svg, .ai, .eps)'}
        </label>
        <p className="text-xs text-gray-500 mt-2">
          SVG files become color-editable. AI/EPS show as a non-editable vector preview.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3" data-testid={`vector-design-editor-${itemIndex}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-800">
            {vd.filename || 'Vector design'}
          </p>
          <p className="text-xs text-gray-500">
            {isSvg ? 'SVG · click any color to edit' : 'Vector preview (not color-editable)'}
          </p>
        </div>
        <div className="flex gap-2">
          {isSvg && vd.original_svg && vd.svg_content !== vd.original_svg && !locked && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
              data-testid={`vector-design-reset-${itemIndex}`}
            >
              <RotateCcw size={14} className="mr-1" />
              Reset
            </Button>
          )}
          {isAdmin && (
            <>
              <input
                type="file"
                accept=".svg,image/svg+xml,.ai,.eps,application/postscript,application/illustrator,application/pdf"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                style={{ display: 'none' }}
                id={`vd-replace-${itemIndex}`}
                data-testid={`vector-design-replace-input-${itemIndex}`}
              />
              <label
                htmlFor={`vd-replace-${itemIndex}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 hover:bg-gray-50 rounded-md cursor-pointer text-xs"
                data-testid={`vector-design-replace-btn-${itemIndex}`}
              >
                <Upload size={12} />
                Replace
              </label>
            </>
          )}
        </div>
      </div>

      {/* Render area */}
      <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center min-h-[180px]">
        {isSvg ? (
          <div
            ref={svgWrapRef}
            style={{ maxWidth: 240, maxHeight: 180, width: '100%' }}
            dangerouslySetInnerHTML={{ __html: safeSvg }}
            data-testid={`vector-design-svg-${itemIndex}`}
          />
        ) : vd.raster_base64 ? (
          <img
            src={`data:${vd.mime_type || 'application/octet-stream'};base64,${vd.raster_base64}`}
            alt={vd.filename}
            style={{ maxWidth: 240, maxHeight: 180 }}
            data-testid={`vector-design-raster-${itemIndex}`}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="text-center text-xs text-gray-500">
            <p className="font-semibold">{vd.filename}</p>
            <p>Vector file — preview unavailable.</p>
            <p>The original file is saved with this proposal.</p>
          </div>
        )}
      </div>

      {/* Color palette (SVG only) — read-only when locked */}
      {isSvg && colors.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1">
            Colors in this design
            {locked && <span className="ml-2 text-[10px] text-gray-500 font-normal">(locked)</span>}
          </p>
          <div className="flex flex-wrap gap-2">
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                disabled={locked}
                onClick={() => { if (!locked) setPicker({ oldColor: c, newColor: c }); }}
                className={
                  'group relative w-10 h-10 rounded-md border-2 border-gray-300 transition ' +
                  (locked ? 'opacity-70 cursor-not-allowed' : 'hover:border-indigo-500')
                }
                style={{ background: c }}
                title={locked ? c : `Click to recolor ${c}`}
                data-testid={`vector-design-swatch-${itemIndex}-${c}`}
              >
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-mono text-gray-500 group-hover:text-indigo-600 whitespace-nowrap">
                  {c}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Inline color picker */}
      {picker && (
        <div className="border border-indigo-200 bg-indigo-50 rounded-md p-3 flex items-center gap-3 mt-6" data-testid={`vector-design-picker-${itemIndex}`}>
          <div
            className="w-8 h-8 rounded border-2 border-white shadow"
            style={{ background: picker.oldColor }}
          />
          <span className="text-sm text-gray-600">→</span>
          <input
            type="color"
            value={picker.newColor}
            onChange={(e) => setPicker({ ...picker, newColor: e.target.value })}
            className="w-12 h-10 cursor-pointer rounded border border-gray-300"
            data-testid={`vector-design-color-input-${itemIndex}`}
          />
          <input
            type="text"
            value={picker.newColor}
            onChange={(e) => {
              const v = e.target.value;
              if (HEX_RE.test(v) || v === '' || /^#[0-9a-fA-F]{0,6}$/.test(v)) {
                setPicker({ ...picker, newColor: v });
              }
            }}
            className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded"
            placeholder="#000000"
            data-testid={`vector-design-hex-input-${itemIndex}`}
          />
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPicker(null)}
              data-testid={`vector-design-cancel-${itemIndex}`}
            >
              <X size={14} />
            </Button>
            <Button
              size="sm"
              disabled={saving || !HEX_RE.test(picker.newColor) || picker.newColor === picker.oldColor}
              onClick={() => applyColorChange(picker.oldColor, picker.newColor)}
              data-testid={`vector-design-apply-${itemIndex}`}
            >
              {saving ? 'Saving…' : 'Apply'}
            </Button>
          </div>
        </div>
      )}

      {/* Per-location placement controls (admin only, SVG only) */}
      {isAdmin && isSvg && locations.length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
              <Move size={12} />
              Placement
            </p>
            <div className="flex flex-wrap gap-1">
              {locations.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  className={
                    'px-2 py-1 text-[11px] rounded ' +
                    (activeLoc === loc
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                  }
                  onClick={() => setActiveLoc(loc)}
                  data-testid={`vector-design-loc-${itemIndex}-${loc}`}
                >
                  {loc.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs">
            {['x', 'y', 'scale', 'rotation'].map((field) => (
              <label key={field} className="flex flex-col gap-0.5">
                <span className="text-gray-500 capitalize">{field}</span>
                <input
                  type="number"
                  value={placement[field] ?? 0}
                  step={field === 'rotation' ? 5 : 1}
                  className="w-full px-2 py-1 border border-gray-300 rounded font-mono"
                  onChange={(e) => {
                    const v = parseFloat(e.target.value || '0');
                    setPendingPlacement((p) => ({
                      loc: activeLoc,
                      values: { ...(p && p.loc === activeLoc ? p.values : {}), [field]: v },
                    }));
                  }}
                  onBlur={(e) => {
                    const next = { ...placement, [field]: parseFloat(e.target.value || '0') };
                    savePlacement(next);
                  }}
                  data-testid={`vector-design-placement-${itemIndex}-${activeLoc}-${field}`}
                />
              </label>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-1">
            x/y/scale in % of garment. Production team uses these for the print-ready layout.
          </p>
        </div>
      )}
    </div>
  );
};

export default VectorDesignEditor;
